import { FC } from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';

import { SearchIcon } from '../../../../../components/Icons';
import {
  DiscoverExploreSearchFormData,
  DISCOVER_EXPLORE_SEARCH_INITIAL_VALUES,
  DISCOVER_TOUR_DURATION_MAX,
  DISCOVER_TOUR_DURATION_MIN,
  parseDurationDaysValue,
} from '../../types/discoverExploreSearch.types';

import styles from './DiscoverTabSearchForm.module.scss';
import HotelLocationField from '../HotelSearchForm/HotelLocationField';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomButton from '../../../../../common/CustomButton';
import { DurationsIcon } from '../../../../../components/Icons/DurationsIcons';
import DiscoverDurationDaysField from '../DiscoverDurationDaysField/DiscoverDurationDaysField';

type DiscoverTabSearchFormProps = {
  onSubmit?: (values: DiscoverExploreSearchFormData) => void | Promise<void>;
  isLoading?: boolean;
  isMobileLayout?: boolean;
};

const DiscoverTabSearchForm: FC<DiscoverTabSearchFormProps> = ({
  onSubmit,
  isLoading = false,
  isMobileLayout = false,
}) => {
  const validate = (values: DiscoverExploreSearchFormData) => {
    const errors: Partial<Record<keyof DiscoverExploreSearchFormData, string>> = {};
    if (!values.from.trim()) {
      errors.from = 'From is required';
    }
    if (!values.to.trim()) {
      errors.to = 'To is required';
    }
    if (values.durationDays.trim() && parseDurationDaysValue(values.durationDays) == null) {
      errors.durationDays = `Enter ${DISCOVER_TOUR_DURATION_MIN}–${DISCOVER_TOUR_DURATION_MAX} days`;
    }
    return errors;
  };

  return (
    <Box className={styles.discoverTabSearchFormContainer}>
      <Formik
        initialValues={DISCOVER_EXPLORE_SEARCH_INITIAL_VALUES}
        validate={validate}
        onSubmit={async (values, { resetForm }) => {
          await Promise.resolve(onSubmit?.(values));
          resetForm({
            values: { ...values },
            submitCount: 0,
            touched: {},
            errors: {},
          });
        }}
        enableReinitialize
      >
        {({ values, handleChange, errors, submitCount }) => {
          const showSubmitErrors = submitCount > 0;
          const reserveDesktopSlots = !isMobileLayout;

          return (
            <Form className={styles.tripForm}>
              <Box className={styles.formGrid}>
                <Box className={styles.formField}>
                  <HotelLocationField
                    name="from"
                    value={values.from}
                    onChange={handleChange}
                    error={showSubmitErrors && errors.from ? errors.from : undefined}
                    inputLabel="From"
                    placeholder="City or region"
                    discoverChrome
                    compactForMobile={isMobileLayout}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <HotelLocationField
                    name="to"
                    value={values.to}
                    onChange={handleChange}
                    error={showSubmitErrors && errors.to ? errors.to : undefined}
                    inputLabel="To"
                    placeholder="City or region"
                    discoverChrome
                    compactForMobile={isMobileLayout}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <CustomReactDatePicker
                    name="depart"
                    inputLabel="Depart (optional)"
                    value={values.depart}
                    onChange={handleChange}
                    error={showSubmitErrors && !!errors.depart}
                    helperText={showSubmitErrors && errors.depart ? errors.depart : ''}
                    dateFormat="MMM d, yyyy"
                    calendarPreset="flightDiscover"
                    reserveHelperSpace={reserveDesktopSlots}
                    compactForMobile={isMobileLayout}
                    allowClear
                  />
                </Box>

                <Box className={styles.formField}>
                  <DiscoverDurationDaysField
                    name="durationDays"
                    inputLabel="Duration (optional)"
                    value={values.durationDays}
                    onChange={handleChange}
                    error={showSubmitErrors && errors.durationDays ? errors.durationDays : undefined}
                    startIcon={<DurationsIcon size={20} />}
                    compactForMobile={isMobileLayout}
                    allowClear
                    placeholder="Enter days"
                  />
                </Box>

                <Box
                  className={`${styles.submitButtonWrapper} ${
                    isMobileLayout ? styles.submitButtonWrapperWide : ''
                  }`}
                >
                  <CustomButton
                    btnType="submit"
                    variant="contained"
                    loading={isLoading}
                    startIcon={<SearchIcon color="#FFFFFF" size={24} />}
                  >
                    {isMobileLayout ? 'Search' : ''}
                  </CustomButton>
                  {reserveDesktopSlots ? (
                    <Box
                      className={styles.fieldErrorReserve}
                      aria-hidden
                    />
                  ) : null}
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default DiscoverTabSearchForm;
